import { InputHTMLAttributes, forwardRef } from 'react'
import { motion } from 'framer-motion'
import MotionDiv from '@/components/common/Motion/MotionDiv'

interface ToggleInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix'> {
  label?: string
  error?: string
  className?: string
  wrapperClassName?: string
  defaultChecked?: boolean
}

export const ToggleInput = forwardRef<HTMLInputElement, ToggleInputProps>(
  ({ label, error, disabled, className = '', wrapperClassName = '', defaultChecked = false, ...props }, ref) => {
    return (
      <MotionDiv className={`flex flex-row ${wrapperClassName}`}>
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            ref={ref}
            className="sr-only peer"
            defaultChecked={defaultChecked}
            disabled={disabled}
            {...props}
          />
          <div className={`
            relative w-11 h-6
            peer-focus:outline-none
            rounded-full
            peer
            peer-checked:after:translate-x-full
            peer-checked:bg-blue-600
            rtl:peer-checked:after:-translate-x-full
            bg-gray-100
            after:content-['']
            after:absolute
            after:top-[0.125rem]
            after:start-[0.125rem]
            after:bg-white
            after:rounded-full
            after:h-5
            after:w-5
            after:transition-all
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            ${className}
          `}>
          </div>
          {label && (
            <span className="ms-3 text-sm font-medium text-zinc-700">
              {label}
            </span>
          )}
        </label>
        {error && (
          <motion.span
            key={error}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-sm text-red-500 ml-3">
            {error}
          </motion.span>
        )}
      </MotionDiv>
    )
  }
)

ToggleInput.displayName = 'ToggleInput'

export default ToggleInput
